import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { daysAgo, PlacementId } from "@sp-crm/core";
import { InputFormBlock } from "components/app/input-form-block";
import { InlineFeedback } from "components/feedback/inline-feedback";
import { Header } from "components/header";
import { AutosavingInput } from "components/ui/autosaving-input";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { AI_SUMMARY_FEEDBACK_OPTIONS, useClientAiSummary } from "./use-client-ai-summary";

interface AiClientSummaryProps {
    clientId: PlacementId;
}

export const AiSummary: React.FC<AiClientSummaryProps> = ({ clientId }) => {
    const {
        summary,
        isLoading,
        isRemoving,
        isSubmittingFeedback,
        error,
        feedbackGiven,
        showRemoveButton,
        generate,
        updateText,
        submitPositiveFeedback,
        startNegativeFeedback,
        submitNegativeFeedback,
        removeSummary,
    } = useClientAiSummary(clientId);

    return (
        <InputFormBlock>
            <Header
                iconName="29_ClientSummary"
                action={
                    <SecondaryButton
                        disabled={isLoading}
                        onClick={generate}
                        className="flex items-center space-x-2">
                        {isLoading ? <Spinner /> : <ArrowPathIcon className="w-4 h-4" />}
                        {summary ? (
                            <span className="text-sm">Regenerate Summary</span>
                        ) : (
                            <span className="text-sm">Generate Summary</span>
                        )}
                    </SecondaryButton>
                }>
                AI Summary
            </Header>

            {error && (
                <div className="text-red-600 text-sm mb-4 p-3 bg-red-50 rounded-md">
                    {error}
                </div>
            )}

            {summary ? (
                <div className="space-y-2">
                    <AutosavingInput
                        initial={summary.text}
                        onCommit={updateText}
                        multiLine
                        autoGrow
                        rows={4}
                    />

                    <div className="flex items-start justify-between">
                        <div>
                            <p className="text-sm text-gray-500">
                                Generated {daysAgo(summary.dateAdded)}
                            </p>
                            {summary.editedAt ? (
                                <p className="text-sm text-gray-500">
                                    Edited by {summary.editedByName} on{" "}
                                    {daysAgo(summary.editedAt)}
                                </p>
                            ) : null}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <InlineFeedback
                                negativeFeedbackPrompt="What could be improved?"
                                negativeFeedbackOptions={AI_SUMMARY_FEEDBACK_OPTIONS}
                                submitted={feedbackGiven}
                                submitting={isSubmittingFeedback}
                                positiveAriaLabel="This summary is helpful"
                                negativeAriaLabel="This summary needs improvement"
                                onPositive={submitPositiveFeedback}
                                onNegativeIntent={startNegativeFeedback}
                                onNegativeSubmit={submitNegativeFeedback}
                            />
                            {showRemoveButton && (
                                <button
                                    type="button"
                                    onClick={removeSummary}
                                    disabled={isRemoving}
                                    className="text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50">
                                    {isRemoving ? "Removing..." : "Remove Summary"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <p className="text-gray-600 text-sm italic">
                    Generate an AI-powered summary of this client&apos;s information,
                    notes, and communications.
                </p>
            )}
        </InputFormBlock>
    );
};
